<script setup lang="ts">
import type { ContentEvent } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircle, faCircleCheck } from '@fortawesome/pro-solid-svg-icons'
import { resolveUserName } from '~/utils/reportUtils'
import { useTime } from '~/composables/useTime'

const props = defineProps<{
  groupMember: GroupMember
  userActivity?: ContentEvent
  groupMembers?: GroupMember[]
}>()

const { t } = useI18n()
const { formatReportTimestamp } = useTime()

const displayName = computed(() => {
  return resolveUserName(props.groupMember)
})

const timestamp = computed(() => {
  return formatReportTimestamp(props.userActivity?.providedTimestamp) || null
})

const hasActivity = computed(() => {
  return !!props.userActivity
})

const isActivityCompleted = computed(() => {
  return !!(props.userActivity?.providedTimestamp)
})
</script>

<template>
  <div class="border-green-40 flex w-full items-stretch overflow-hidden rounded-lg border bg-white">
    <div class="flex flex-1 flex-col gap-2 p-5 md:flex-row md:items-center md:gap-4">
      <div class="min-w-0 flex-1">
        <div class="text-green-40 min-h-5 text-base font-medium">
          <p class="break-words">
            {{ displayName }}
          </p>
        </div>
        <div class="text-green-40 mt-1 text-sm">
          <p class="break-words">
            {{ timestamp ? t('reports.labels.visitedAt', { timestamp }) : '\u00A0' }}
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span class="text-green-40 text-sm font-medium">{{ t('reports.labels.visitStatus') }}</span>
        <FontAwesomeIcon
          :icon="isActivityCompleted ? faCircleCheck : faCircle"
          :class="isActivityCompleted ? 'text-green-40' : 'text-gray-30'"
          :title="isActivityCompleted ? t('reports.labels.visitedTitle') : t('reports.labels.notVisitedTitle')"
          class="h-6 w-6 text-xl md:h-8 md:w-8 md:text-2xl"
          aria-hidden="true"
        />
        <span class="sr-only">{{ isActivityCompleted ? t('reports.labels.visitedTitle') : t('reports.labels.notVisitedTitle') }}</span>
      </div>
    </div>
  </div>
</template>
